import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, Popover } from '@mui/material';
// eslint-disable-next-line import/no-extraneous-dependencies
import { MoreHorizontal, MoreVertical } from 'lucide-react';

// Shared row-action ("kebab") menu — the app-wide dropdown used on table rows
// and cards (Team Engagement, Insider Threats, ...). Update it here and every
// usage updates. Styling mirrors the prototype's shadcn dropdown-menu:
// a rounded-md bordered panel with shadow-md; px-2 py-1.5 text-sm rounded-sm
// items with a #e9ebef accent hover. Items flagged `destructive` render in red
// (#d4183d) with a 10% red hover — reserve that for Delete-type actions only.
const menuPaperSx = {
  marginTop: '4px',
  minWidth: '8rem',
  borderRadius: '6px',
  border: '1px solid #e0e1e6',
  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -2px rgba(0, 0, 0, 0.1)',
  padding: '4px',
};

const baseItemSx = {
  fontSize: 14,
  padding: '6px 8px',
  borderRadius: '4px',
  minHeight: 'auto',
  color: '#0a0a0a',
  '&:hover': { backgroundColor: '#e9ebef', color: '#030213' },
  '&.Mui-disabled': { opacity: 0.5 },
};

const destructiveItemSx = {
  ...baseItemSx,
  color: '#d4183d',
  '&:hover': { backgroundColor: 'rgba(212, 24, 61, 0.1)', color: '#d4183d' },
};

const DEFAULT_TRIGGER = 'w-6 h-6 rounded text-ink-500 hover:bg-surface-1 flex '
  + 'items-center justify-center transition bg-transparent border-0 p-0';

function RowActionMenu({
  items, orientation, iconSize, ariaLabel, triggerClassName,
}) {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const Icon = orientation === 'vertical' ? MoreVertical : MoreHorizontal;
  const close = () => setAnchorEl(null);

  return (
    <div className="relative inline-block">
      <button
        type="button"
        aria-label={ariaLabel}
        onClick={(e) => {
          e.stopPropagation();
          setAnchorEl(e.currentTarget);
        }}
        className={triggerClassName}
      >
        <Icon size={iconSize} />
      </button>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ sx: menuPaperSx }}
      >
        {items.map((item) => (
          <MenuItem
            key={item.label}
            disabled={item.disabled}
            sx={item.destructive ? destructiveItemSx : baseItemSx}
            onClick={(e) => {
              e.stopPropagation();
              close();
              item.onClick?.();
            }}
          >
            {item.label}
          </MenuItem>
        ))}
      </Popover>
    </div>
  );
}

RowActionMenu.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func,
    disabled: PropTypes.bool,
    destructive: PropTypes.bool,
  })).isRequired,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  iconSize: PropTypes.number,
  ariaLabel: PropTypes.string,
  triggerClassName: PropTypes.string,
};

RowActionMenu.defaultProps = {
  orientation: 'horizontal',
  iconSize: 14,
  ariaLabel: 'Open actions',
  triggerClassName: DEFAULT_TRIGGER,
};

export default RowActionMenu;
